import "./chart.scss";

// Icons
import collapseIcon from "assets/images/icons/btn_topology_collapse.svg";
import expandIcon from "assets/images/icons/btn_topology_expand.svg";

import { useState, useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { Box } from "@mui/material";
import JSMind from "jsmind";
import { cloneDeep } from "lodash";

import { chartOption } from "../const";

const findNode = (tree, nodeId, statistic) => {
  return tree.forEach((node) => {
    if (node.id === nodeId) {
      statistic.node = cloneDeep(node);
      return;
    }

    if (node.children?.length > 0) {
      findNode(node.children, nodeId, statistic);
    }
  });
}

const redrawLines = (chartId, tooltipRef, tree, rawTopologyData) => {
  const container = document.querySelector(`#${chartId}`);
  const paths = document.querySelectorAll(`#${chartId} path`);

  paths.forEach((path) => {
    // Set stroke
    let originStroke = path.attributes["origin-stroke"]?.value;
    let ary = [];

    if (originStroke) {
      ary = path.attributes["origin-stroke"].value.split("|");
    } else {
      path.setAttribute("origin-stroke", path.attributes["stroke"].value);
      ary = path.attributes["stroke"].value.split("|");
    }

    const nodeId = ary[1];
    const statistic = { node: null };
    findNode(tree, nodeId, statistic);
    const node = statistic.node;

    if (node?.match) {
      path.setAttribute("stroke", ary[2]);
    } else {
      path.setAttribute("stroke", '#d5d5d5');
    }

    if (ary.length === 4) {
      path.setAttribute("stroke-dasharray", "3,3");
      path.setAttribute("stroke-width", "2");
    }

    // Make the line curve smoother and resolve the issue of disappearing lines on Firefox
    let originD = path.attributes["origin-d"]?.value;
    let d = path.attributes["d"].value;
    if (!originD) {
      path.setAttribute("origin-d", d);
    } else {
      d = originD;
    }

    const originDAry = d.split(",");

    const firstOne = originDAry[0].split(" ");
    firstOne[4] = +firstOne[4] + 2;

    const secondOne = originDAry[1].split(" ");
    secondOne[secondOne.length - 2] = +secondOne[secondOne.length - 2] + -30;

    const thirdOne = originDAry[2].split(" ");
    thirdOne[thirdOne.length - 2] = +thirdOne[thirdOne.length - 2] + 0;

    const combined = `${firstOne.join(" ")}, ${secondOne.join(" ")}, ${thirdOne.join(" ")}`;
    path.setAttribute("d", combined);


    // set id
    path.setAttribute("id", `${chartId}_path_${ary[1]}`);
  });

  paths.forEach((path) => {
    path.addEventListener("mouseover", (event) => {
      const target = event.target;
      const nodeId = target.id.split("_")[2];
      const tooltip = tooltipRef.current;
      const node = rawTopologyData.find((item) => item.id === nodeId);

      if (tooltip && node.connection) {
        tooltip.style.position = "absolute";
        tooltip.style.display = "block";

        if (container.classList.contains("topology-fullscreen")) {
          tooltip.style.left = `${event.screenX + 20}px`;
          tooltip.style.top = `${event.screenY - 20}px`;
        } else {
          tooltip.style.left = `${event.pageX - 80}px`;
          tooltip.style.top = `${event.pageY - 80}px`;
        }

        tooltip.innerHTML = ReactDOMServer.renderToString(
          <div className="topology-tooltip">
            <div className="topology-tip-item">
              <span>Port</span>
              <span>
                #{node.connection.parentPort} &#60; &#62; #{node.connection.childPort}
              </span>
            </div>
            <div className="topology-tip-item">
              <span>Speed</span>
              <span>{node.connection.speed}</span>
            </div>
          </div>
        );
      }
    });

    path.addEventListener("mouseout", () => {
      const tooltip = tooltipRef.current;
      if (tooltip?.style?.display === "block") {
        tooltip.style.display = "none";
      }
    });
  });
};

const redrawButtons = (chartId, tree) => {
  const buttons = document.querySelectorAll(`#${chartId} jmexpander`);

  buttons.forEach((button) => {
    const text = button.textContent;

    // Check the button type
    if (text === "-") {
      button.setAttribute("alt", "Collapse");
      button.style.backgroundImage = `url(${collapseIcon})`;
    } else {
      button.setAttribute("alt", "Expand");
      button.style.backgroundImage = `url(${expandIcon})`;
    }

    // Check the button status
    // If the node has children and all children are matched, the button is enabled
    // Otherwise, the button is disabled
    const nodeId = button.getAttribute("nodeid");
    const statistic = { node: null };
    findNode(tree, nodeId, statistic);
    const node = statistic.node;

    let match = true;
    if (node?.children?.length > 0) {
      node.children.forEach((child) => {
        if (!child.match) {
          match = false;
        }
      });
    } else {
      match = false;
    }

    if (!match) {
      button.style.filter = "grayscale(1)";
    }

    // Event listener
    button.addEventListener("click", (event) => {
      const target = event.target;
      const alt = target.getAttribute("alt");

      if (alt === "Collapse") {
        button.setAttribute("alt", "Expand");
        button.style.backgroundImage = `url(${expandIcon})`;
      } else {
        button.setAttribute("alt", "Collapse");
        button.style.backgroundImage = `url(${collapseIcon})`;
      }
    });
  });
};

const Chart = (props) => {
  const {
    treeIndex,
    chartId,
    rawTopologyData,
    tree,
    statistic,
    tooltipRef,
    onToggleSlider
  } = props;

  // State
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Method
  const handleZoomIn = () => {
    let newZoomLevel = zoomLevel + 0.1;
    if (newZoomLevel >= 1.0) {
      newZoomLevel = 1.0;
    }

    const chartSvg = document.querySelector(`#${chartId} svg`);
    const chartJm = document.querySelector(`#${chartId} jmnodes`);
    chartSvg.style.zoom = newZoomLevel.toString();
    chartJm.style.zoom = newZoomLevel.toString();

    setZoomLevel(newZoomLevel);
    redrawLines(chartId, tooltipRef, tree, rawTopologyData);
  };

  const handleZoomOut = () => {
    let newZoomLevel = zoomLevel - 0.1;
    if (newZoomLevel <= 0.1) {
      newZoomLevel = 0.1;
    }

    const chartSvg = document.querySelector(`#${chartId} svg`);
    const chartJm = document.querySelector(`#${chartId} jmnodes`);
    chartSvg.style.zoom = newZoomLevel.toString();
    chartJm.style.zoom = newZoomLevel.toString();

    setZoomLevel(newZoomLevel);
    redrawLines(chartId, tooltipRef, tree, rawTopologyData);
  };

  const toggleFullscreen = () => {
    const updatedIsFullscreen = !isFullscreen;
    setIsFullscreen(updatedIsFullscreen);
    const chartContainer = document.querySelector(`#${chartId}`);

    /*  Fullscreen
     ** True: zoom level from 1 to 2
     ** False: zoom level from 1 to 2
     */

    if (updatedIsFullscreen) {
      chartContainer.classList.add("topology-fullscreen");
      mapRef.current.requestFullscreen();
    } else {
      chartContainer.classList.remove("topology-fullscreen");
      document.exitFullscreen();
    }

    redrawLines(chartId, tooltipRef, tree, rawTopologyData);
    redrawButtons(chartId, tree);
  };

  // Side effect
  useEffect(() => {
    if (!chartId || !tree) {
      return;
    }

    const updatedChartOption = cloneDeep(chartOption);
    updatedChartOption.container = chartId;

    // Remove old chart
    const chartContainer = document.querySelector(`#${chartId} .jsmind-inner.jmnode-overflow-hidden`);
    if (chartContainer) {
      chartContainer.remove();
    }

    // Create new chart
    const mapObj = new JSMind(updatedChartOption);
    mapObj.show({
      meta: {
        name: "jsMind",
      },
      format: "node_tree",
      data: tree[0],
    });
    mapInstanceRef.current = mapObj;

    // Prevent default scroll
    const container = document.getElementById(chartId);
    container.addEventListener(
      "wheel",
      function () {
        redrawLines(chartId, tooltipRef, tree, rawTopologyData);
      },
      { passive: false }
    );

    // Redraw lines
    redrawLines(chartId, tooltipRef, tree, rawTopologyData);

    // Redraw buttons
    redrawButtons(chartId, tree);

    // Set the minimum zoom level
    const chartSvg = document.querySelector(`#${chartId} svg`);
    const chartJm = document.querySelector(`#${chartId} jmnodes`);

    if (zoomLevel === null) {
      // The value of 539 is a max height of a chart
      let zoomNumber = (530 / + chartSvg.attributes['height'].value).toFixed(1).toString();
      if (zoomNumber === "0.0") {
        zoomNumber = "0.1";
      }

      chartSvg.style.zoom = zoomNumber;
      chartJm.style.zoom = zoomNumber;
      setZoomLevel(+zoomNumber);
    } else {
      chartSvg.style.zoom = zoomLevel.toString();
      chartJm.style.zoom = zoomLevel.toString();
    }

    redrawLines(chartId, tooltipRef, tree, rawTopologyData);

    // Event listener
    mapObj.add_event_listener(function (type, data) {
      redrawLines(chartId, tooltipRef, tree, rawTopologyData);
      redrawButtons(chartId, tree);

      if (type === 4) {
        if (data.node.indexOf("client") === -1) {
          onToggleSlider(data.node);
        }
      }

      // if (type === 4) {
      //   if (data.node.indexOf("client") === -1) {
      //     console.log("Toggle slider from chart:", data.node);
      //     const chart = document.querySelector(`#${chartId}`);
      //     const node = chart.querySelector(`jmnode[nodeid='${data.node}']`);
      //     const nodeIcon = node.querySelector('div[class^="node_icon__"]');

      //     nodeIcon.addEventListener("click", () => {
      //       console.log("Click img:", data.node);
      //       onToggleSlider(data.node);
      //     });
      //   }
      // }
    });

    // Fullscreen
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === mapRef.current);

      if (!document.fullscreenElement) {
        const chartContainer = document.querySelector(`#${chartId}`);
        chartContainer.classList.remove("topology-fullscreen");
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    // Resize
    const handleResize = () => {
      mapInstanceRef.current.resize();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    chartId,
    onToggleSlider,
    rawTopologyData,
    statistic.maxChildren,
    statistic.maxLevel,
    tooltipRef,
    tree,
    zoomLevel]
  );

  return (
    <>
      <div
        id={chartId}
        className='jsmind topology-chart'
        ref={mapRef}
      >
        <div className="topology-action">
          <div className={`button ${zoomLevel === 1.0 ? 'zoom-in-disabled' : 'zoom-in'}`} onClick={handleZoomIn} />
          <div className={`button ${zoomLevel === 0.1 ? 'zoom-out-disabled' : 'zoom-out'}`} onClick={handleZoomOut} />
          <div className="button fullscreen" onClick={toggleFullscreen} />
        </div>
        {
          isFullscreen && (
            <>
              <Box ref={tooltipRef} className="topology-tip" />
            </>
          )
        }
      </div>
      {
        !isFullscreen && (
          <>
            <Box ref={tooltipRef} className="topology-tip" />
          </>
        )
      }
    </>
  );
}

export default Chart;